import React from "react";
import { logError } from "../utils/errorLib";
import Header from "./Header";
import Button from "./buttons/Button";

export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    logError(error, errorInfo);
  }

  render() {
    return this.state.hasError ? (
      <div className="flex h-screen overflow-hidden">
        <div className="lg:w-64 relative">
          {/* Sidebar backdrop (mobile only) */}
          <div
            className={`fixed inset-0 bg-gray-900 bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
              true ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            aria-hidden="true"
          ></div>

          {/* Sidebar */}
          <div
            id="sidebar"
            className={`absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 transform h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar w-64 flex-shrink-0 bg-gray-800 p-4 transition-transform duration-200 ease-in-out ${
              true ? "translate-x-0" : "-translate-x-64"
            }`}
          >
            {/* Sidebar header */}
            <div className="flex justify-between mb-10 pr-3 sm:px-2">
              {/* Close button */}
              <button
                className="lg:hidden text-gray-500 hover:text-gray-400"
                aria-controls="sidebar"
              >
                <span className="sr-only">Close sidebar</span>
                <svg
                  className="w-6 h-6 fill-current"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
                </svg>
              </button>
              {/* Logo */}
              <span className="block">
                <svg width="32" height="32" viewBox="0 0 32 32">
                  <defs>
                    <linearGradient
                      x1="28.538%"
                      y1="20.229%"
                      x2="100%"
                      y2="108.156%"
                      id="logo-a"
                    >
                      <stop stopColor="#A5B4FC" stopOpacity="0" offset="0%" />
                      <stop stopColor="#A5B4FC" offset="100%" />
                    </linearGradient>
                    <linearGradient
                      x1="88.638%"
                      y1="29.267%"
                      x2="22.42%"
                      y2="100%"
                      id="logo-b"
                    >
                      <stop stopColor="#38BDF8" stopOpacity="0" offset="0%" />
                      <stop stopColor="#38BDF8" offset="100%" />
                    </linearGradient>
                  </defs>
                  <rect fill="#6366F1" width="32" height="32" rx="16" />
                  <path
                    d="M18.277.16C26.035 1.267 32 7.938 32 16c0 8.837-7.163 16-16 16a15.937 15.937 0 01-10.426-3.863L18.277.161z"
                    fill="#4F46E5"
                  />
                  <path
                    d="M7.404 2.503l18.339 26.19A15.93 15.93 0 0116 32C7.163 32 0 24.837 0 16 0 10.327 2.952 5.344 7.404 2.503z"
                    fill="url(#logo-a)"
                  />
                  <path
                    d="M2.223 24.14L29.777 7.86A15.926 15.926 0 0132 16c0 8.837-7.163 16-16 16-5.864 0-10.991-3.154-13.777-7.86z"
                    fill="url(#logo-b)"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          {/*  Site header */}
          <Header isSidebarOpen={true} setIsSidebarOpen={() => ({})} />
          {/* Content area */}
          {/* <Main> */}
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className="flex flex-col items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="285.57"
                width="440.67"
              >
                <defs>
                  <clipPath id="e">
                    <path d="M883.16 3681.84l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73z" />
                  </clipPath>
                  <clipPath id="f">
                    <path d="M881.85 3683.86l-3.7 104.31c-.42 11.83-1.1 20.24-2.05 25.24-.95 4.93-3.43 10.38-7.43 16.34-4.01 5.9-9.2 10.88-15.6 14.97-6.31 4-13.72 6.99-22.22 8.94-9.4 2.17-17.66 2.54-24.75 1.11-7.1-1.42-12.34-4.2-15.73-8.33s-5.32-8.84-5.77-14.16c-.45-5.37-.37-16.87.26-34.48l3.27-92.35 40.97-9.44-3.14 88.56c-.24 6.81-.04 11.1.6 12.86.72 1.69 2.21 2.27 4.48 1.75 2.6-.6 4.29-2.01 5.07-4.23.86-2.3 1.42-7.34 1.7-15.12l3.07-86.53z" />
                  </clipPath>
                  <clipPath id="g">
                    <path d="M891.96 3681.61l-5.54 156.08-40.97 9.44 2.03-57.1-12.27 2.82-2.02 57.1-40.97 9.44 5.53-156.08 40.97-9.44-1.69 47.74 12.26-2.82 1.7-47.74z" />
                  </clipPath>
                  <clipPath id="h">
                    <path d="M852.56 3688.51c-16.75 4.02-33.48 13.12-47.78 25.4l-1.68 44.13c14.34-9.97 27.67-27.13 44.56-28.25 2.37 3.73-4.84 8.93-8.02 11.72-14.23 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.1-2.87 24.2-5.09 36.31-8.57.13-15.46-2.77-23.01 4.25-35 16.16-15.34 34.93-34.7 35.65-55.96.4-20.76-19.83-31.98-40.04-26.67zm-36.5 134.99c-1.96 12.08-1 23.52-2.97 35.62l38.21-8.72c2.07-12.72 1.21-24.76 3.28-37.48l-38.2 8.72-.18 1.05z" />
                  </clipPath>
                  <clipPath id="a">
                    <path
                      fill="none"
                      d="M778.71 3894.54l440.67-105.84v185.49L778.7 4074.27z"
                    />
                  </clipPath>
                </defs>
                <g
                  clipPath="url(#a)"
                  fill="none"
                  transform="translate(-778.71 -3788.7)"
                >
                  <g fill="#000">
                    <path d="M1176.81 3816.95c-16.74 4.02-33.48 13.11-47.78 25.4l-1.68 44.12c14.34-9.97 27.67-27.12 44.56-28.24 2.37 3.72-4.84 8.92-8.02 11.72-14.22 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.11-2.88 24.2-5.1 36.32-8.57.12-15.46-2.77-23.02 4.24-35 16.16-15.34 34.94-34.7 35.65-55.96.41-20.76-19.82-31.98-40.04-26.67zm-36.49 134.98c-1.97 12.08-1 23.53-2.98 35.62l38.22-8.71c2.07-12.72 1.2-24.76 3.27-37.48l-38.2 8.71-.18 1.06zm-23.6-114.66l-5.54 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zm-110.08 25.35l-3.7 104.31c-.41 11.83-1.1 20.24-2.04 25.24-.95 4.93-3.43 10.38-7.44 16.34-4 5.9-9.2 10.88-15.59 14.97-6.31 4-13.72 6.98-22.22 8.94-9.41 2.17-17.66 2.54-24.75 1.11-7.1-1.42-12.34-4.2-15.73-8.33s-5.32-8.84-5.77-14.16c-.46-5.37-.37-16.87.25-34.48l3.28-92.35 40.97-9.44-3.14 88.56c-.24 6.81-.04 11.1.6 12.86.72 1.69 2.2 2.27 4.48 1.75 2.6-.6 4.29-2.01 5.07-4.23.86-2.3 1.42-7.34 1.7-15.12l3.06-86.53zm-109.48 25.22l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zM883.16 3681.84l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zM992.64 3656.62l-3.7 104.31c-.41 11.83-1.1 20.24-2.04 25.24-.95 4.93-3.43 10.38-7.44 16.34-4 5.9-9.2 10.88-15.59 14.97-6.31 4-13.72 6.98-22.22 8.94-9.41 2.17-17.66 2.54-24.75 1.11-7.1-1.42-12.34-4.2-15.73-8.33s-5.32-8.84-5.77-14.16c-.46-5.37-.37-16.87.25-34.48l3.28-92.35 40.97-9.44-3.14 88.56c-.24 6.81-.04 11.1.6 12.86.72 1.69 2.2 2.27 4.48 1.75 2.6-.6 4.29-2.01 5.07-4.23.86-2.3 1.42-7.34 1.7-15.12l3.06-86.53zM1102.71 3631.27l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zM1162.81 3610.95c-16.74 4.02-33.48 13.11-47.78 25.4l-1.68 44.12c14.34-9.97 27.67-27.12 44.56-28.24 2.37 3.72-4.84 8.92-8.02 11.72-14.22 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.11-2.88 24.2-5.1 36.32-8.57.12-15.46-2.77-23.02 4.24-35 16.16-15.34 34.94-34.7 35.65-55.96.41-20.76-19.82-31.98-40.04-26.67z" />
                  </g>
                  <g id="d" transform="rotate(-12.93 6439.45 5216.94)">
                    <g id="c">
                      <circle
                        id="b"
                        cx="1228.61"
                        cy="2478.18"
                        r="2.25"
                        fill="red"
                      />
                      <use
                        transform="translate(26.66 57.62) scale(.9783)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(53.32 115.24) scale(.9566)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(79.98 172.85) scale(.9349)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(106.64 230.47) scale(.9132)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(133.3 288.09) scale(.8915)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(159.97 345.7) scale(.8698)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(186.63 403.33) scale(.8481)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(213.29 460.94) scale(.8264)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(239.95 518.56) scale(.8047)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="matrix(.783 0 0 .783 266.6 576.18)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(293.27 633.8) scale(.7613)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(319.93 691.42) scale(.7396)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(346.6 749.03) scale(.7179)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(373.25 806.65) scale(.6962)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(399.91 864.27) scale(.6745)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(426.57 921.89) scale(.6528)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(453.23 979.5) scale(.6311)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(479.9 1037.12) scale(.6094)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(506.56 1094.74) scale(.5877)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="matrix(.566 0 0 .566 533.22 1152.36)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(559.88 1209.98) scale(.5443)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(586.54 1267.6) scale(.5226)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(613.2 1325.21) scale(.5009)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(639.86 1382.83) scale(.4792)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(666.52 1440.45) scale(.4575)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(693.18 1498.07) scale(.4358)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(719.84 1555.68) scale(.4141)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(746.5 1613.3) scale(.3924)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(773.17 1670.92) scale(.3707)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="matrix(.349 0 0 .349 799.83 1728.54)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(826.49 1786.16) scale(.3273)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(853.15 1843.77) scale(.3056)"
                        height="100%"
                        width="100%"
                      />
                      <use
                        transform="translate(879.8 1901.4) scale(.2839)"
                        height="100%"
                        width="100%"
                      />
                    </g>
                    <use
                      transform="translate(4.5)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(8.99)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(13.48)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(17.97)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(22.46)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(26.96)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(31.45)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(35.94)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(40.44)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(44.93)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(49.42)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(53.92)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(58.4)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(62.9)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(67.4)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(71.89)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(76.38)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(80.87)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(85.37)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(89.86)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(94.35)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(98.85)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(103.34)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(107.83)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(112.33)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(116.82)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(121.31)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(125.8)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(130.3)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(134.79)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(139.28)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(143.78)"
                      height="100%"
                      width="100%"
                    />
                    <use
                      transform="translate(148.27)"
                      height="100%"
                      width="100%"
                    />
                  </g>
                  <path
                    d="M842.19 3691.28l-5.6 132.66"
                    stroke="#0f0"
                    strokeWidth=".93"
                  />
                  <g fill="#000">
                    <path d="M992.64 3656.62l-3.7 104.31c-.41 11.83-1.1 20.24-2.04 25.24-.95 4.93-3.43 10.38-7.44 16.34-4 5.9-9.2 10.88-15.59 14.97-6.31 4-13.72 6.98-22.22 8.94-9.41 2.17-17.66 2.54-24.75 1.11-7.1-1.42-12.34-4.2-15.73-8.33s-5.32-8.84-5.77-14.16c-.46-5.37-.37-16.87.25-34.48l3.28-92.35 40.97-9.44-3.14 88.56c-.24 6.81-.04 11.1.6 12.86.72 1.69 2.2 2.27 4.48 1.75 2.6-.6 4.29-2.01 5.07-4.23.86-2.3 1.42-7.34 1.7-15.12l3.06-86.53zM1102.71 3631.27l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zM1162.81 3610.95c-16.74 4.02-33.48 13.11-47.78 25.4l-1.68 44.12c14.34-9.97 27.67-27.12 44.56-28.24 2.37 3.72-4.84 8.92-8.02 11.72-14.22 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.11-2.88 24.2-5.1 36.32-8.57.12-15.46-2.77-23.02 4.24-35 16.16-15.34 34.94-34.7 35.65-55.96.41-20.76-19.82-31.98-40.04-26.67zm-36.49 134.98c-1.97 12.08-1 23.53-2.98 35.62l38.22-8.71c2.07-12.72 1.2-24.76 3.27-37.48l-38.2 8.71-.18 1.06z" />
                  </g>
                  <path
                    d="M1162.81 3810.95c-16.74 4.02-33.48 13.11-47.78 25.4l-1.68 44.12c14.34-9.97 27.67-27.12 44.56-28.24 2.37 3.72-4.84 8.92-8.02 11.72-14.22 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.11-2.88 24.2-5.1 36.32-8.57.12-15.46-2.77-23.02 4.24-35 16.16-15.34 34.94-34.7 35.65-55.96.41-20.76-19.82-31.98-40.04-26.67zm-36.49 134.98c-1.97 12.08-1 23.53-2.98 35.62l38.22-8.71c2.07-12.72 1.2-24.76 3.27-37.48l-38.2 8.71-.18 1.06zm-23.6-114.66l-5.54 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zm-110.08 25.35l-3.7 104.31c-.41 11.83-1.1 20.24-2.04 25.24-.95 4.93-3.43 10.38-7.44 16.34-4 5.9-9.2 10.88-15.59 14.97-6.31 4-13.72 6.98-22.22 8.94-9.41 2.17-17.66 2.54-24.75 1.11-7.1-1.42-12.34-4.2-15.73-8.33s-5.32-8.84-5.77-14.16c-.46-5.37-.37-16.87.25-34.48l3.28-92.35 40.97-9.44-3.14 88.56c-.24 6.81-.04 11.1.6 12.86.72 1.69 2.2 2.27 4.48 1.75 2.6-.6 4.29-2.01 5.07-4.23.86-2.3 1.42-7.34 1.7-15.12l3.06-86.53zm-109.48 25.22l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73z"
                    stroke="#000"
                    strokeWidth="4"
                    fill="#ff0"
                  />
                  <use
                    clipPath="url(#e)"
                    transform="translate(0 200)"
                    height="100%"
                    width="100%"
                  />
                  <use
                    clipPath="url(#f)"
                    transform="translate(110.8 172.76)"
                    height="100%"
                    width="100%"
                  />
                  <use
                    clipPath="url(#g)"
                    transform="translate(210.76 149.66)"
                    height="100%"
                    width="100%"
                  />
                  <path
                    d="M1162.81 3610.95c-16.74 4.02-33.48 13.11-47.78 25.4l-1.68 44.12c14.34-9.97 27.67-27.12 44.56-28.24 2.37 3.72-4.84 8.92-8.02 11.72-14.22 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.11-2.88 24.2-5.1 36.32-8.57.12-15.46-2.77-23.02 4.24-35 16.16-15.34 34.94-34.7 35.65-55.96.41-20.76-19.82-31.98-40.04-26.67zm-36.49 134.98c-1.97 12.08-1 23.53-2.98 35.62l38.22-8.71c2.07-12.72 1.2-24.76 3.27-37.48l-38.2 8.71-.18 1.06z"
                    fill="#000"
                  />
                  <use
                    clipPath="url(#h)"
                    transform="translate(310.25 122.44)"
                    height="100%"
                    width="100%"
                  />
                  <path
                    d="M1162.81 3610.95c-16.74 4.02-33.48 13.11-47.78 25.4l-1.68 44.12c14.34-9.97 27.67-27.12 44.56-28.24 2.37 3.72-4.84 8.92-8.02 11.72-14.22 11.8-16.28 29.03-19.65 45.45-1.2 9.34-1.77 18.58-3.6 27.75 12.11-2.88 24.2-5.1 36.32-8.57.12-15.46-2.77-23.02 4.24-35 16.16-15.34 34.94-34.7 35.65-55.96.41-20.76-19.82-31.98-40.04-26.67zm-36.49 134.98c-1.97 12.08-1 23.53-2.98 35.62l38.22-8.71c2.07-12.72 1.2-24.76 3.27-37.48l-38.2 8.71-.18 1.06zm-23.6-114.66l-5.54 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73zm-110.08 25.35l-3.7 104.31c-.41 11.83-1.1 20.24-2.04 25.24-.95 4.93-3.43 10.38-7.44 16.34-4 5.9-9.2 10.88-15.59 14.97-6.31 4-13.72 6.98-22.22 8.94-9.41 2.17-17.66 2.54-24.75 1.11-7.1-1.42-12.34-4.2-15.73-8.33s-5.32-8.84-5.77-14.16c-.46-5.37-.37-16.87.25-34.48l3.28-92.35 40.97-9.44-3.14 88.56c-.24 6.81-.04 11.1.6 12.86.72 1.69 2.2 2.27 4.48 1.75 2.6-.6 4.29-2.01 5.07-4.23.86-2.3 1.42-7.34 1.7-15.12l3.06-86.53zm-109.48 25.22l-5.53 156.08-40.97 9.43 2.02-57.1-12.26 2.83-2.02 57.1-40.97 9.43 5.53-156.07 40.97-9.44-1.7 47.74 12.27-2.83 1.69-47.73z"
                    fill="#000"
                  />
                </g>
              </svg>
              <h3>
                There was an error. It's okay. It will be automatically reported
                to Devin and he'll figure it out!
              </h3>
              <p className="mb-8">
                Feel free to{" "}
                <a
                  className="border-b border-indigo-800"
                  href="mailto: fitzsimonsdevin@gmail.com"
                >
                  email him.
                </a>
              </p>
              <p>
                In the meantime,{" "}
                <Button
                  variant="create"
                  onClick={() => window.location.reload()}
                >
                  Refresh the Page
                </Button>
              </p>
            </div>
          </div>
          {/* </Main> */}
        </div>
      </div>
    ) : (
      this.props.children
    );
  }
}
